import { action } from 'typesafe-actions';
import React, { PropsWithChildren, ReactElement, useReducer, useCallback, useEffect, useState } from 'react';
import { getServiceHostEndpoint } from '@dxb-web-ui-framework/common/domain/ServiceDiscovery/ServiceDiscoveryService';
import { ServiceId } from '@dxb-web-ui-framework/common/domain/ServiceDiscovery/types';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks';
import { dashboardService } from '../../../../channels/secure-web/services';
import { PartyIdentification } from '../../../card/src/types';
import { CardHeaders } from '../../../userProfile/src/domain/UserInfo/entities';
import { PartyRelationship } from '../../../userProfile/src/domain/UserInfo/entities/PostLoginCardResponseDTO';
import { hasKeycloakAttachedListeners, isKeycloakAuthenticated } from '../utils/keycloakHelpers';
import { keycloak } from '../utils/keycloak';
import { defaultKeycloakProviderState, defaultInitOptions } from './defaultState';
import {
  KeycloakProviderProps,
  KeycloakContextActionTypes,
  AuthClient,
  AuthClientError,
  AuthClientEvent,
  AuthClientTokens,
} from './types';
import { KeycloakDispatchContext } from './KeycloakContext';
import { KeycloakContext } from './KeycloakContext';
import { KeycloakReducer } from './KeycloakReducer';

const ReactKeycloakProvider = ({
  data = defaultKeycloakProviderState,
  isLoadingCheck,
  autoRefreshToken,
  initOptions,
  onEvent,
  loaderComponent,
  children,
}: PropsWithChildren<KeycloakProviderProps<AuthClient>>): ReactElement => {
  const [state, dispatch] = useReducer(KeycloakReducer, data);
  const dispatchCb = useCallback(obj => dispatch(obj), []);
  const [authClient] = useState(keycloak(getServiceHostEndpoint(ServiceId.AUTHENTICATION, '/auth')));

  // event listeners

  // Catch any errors from the events listeners
  const onError = useCallback(
    (event: AuthClientEvent) => (error?: AuthClientError) => {
      // Notify Events listener
      onEvent && onEvent(event, error);
    },
    [onEvent],
  );

  // Set the token values to the provider's state
  const onTokens = useCallback(
    (tokens: AuthClientTokens, tokenParsed?: Keycloak.KeycloakTokenParsed) => {
      if (!tokens.token) {
        return;
      }
      dispatchCb(action(KeycloakContextActionTypes.SetTokens, { ...tokens, tokenParsed }));
      // Authenticate the user if not done previously
      if (!state.isAuthenticated) {
        dispatchCb(
          action(KeycloakContextActionTypes.SetAuthStatus, {
            initialized: true,
            isAuthenticated: true,
            isLoading: false,
          }),
        );
      }
    },
    [dispatchCb, state.isAuthenticated],
  );

  const updateState = useCallback(
    (event: AuthClientEvent) => () => {
      const { initialized: prevInitialized, isAuthenticated: prevAuthenticated, isLoading: prevLoading } = state;
      // Notify Events listener
      onEvent && onEvent(event);
      // Check Loading state
      const isLoading = isLoadingCheck ? isLoadingCheck(authClient) : false;
      // Check if user is authenticated
      const isAuthenticated = isKeycloakAuthenticated(authClient);
      // Avoid double-refresh if state hasn't changed
      if (!prevInitialized || isAuthenticated !== prevAuthenticated || isLoading !== prevLoading) {
        // dispatch action that sets the auth status
        dispatchCb(
          action(KeycloakContextActionTypes.SetAuthStatus, {
            initialized: true,
            isAuthenticated,
            isLoading,
          }),
        );
      }
      // Notify token listener, if any
      const { idToken, refreshToken, token, tokenParsed } = authClient;
      onTokens && onTokens({ idToken, refreshToken, token }, tokenParsed);
    },
    [state, authClient, onEvent, onTokens, isLoadingCheck, dispatchCb],
  );

  // Update token when current token expires or becomes invalid
  const refreshToken = useCallback(
    (event: AuthClientEvent) => () => {
      // Notify Events listener
      onEvent && onEvent(event);

      if (autoRefreshToken !== false) {
        // Refresh Keycloak token
        authClient.updateToken(5);
      }
    },
    [autoRefreshToken, authClient, onEvent],
  );

  useEffect(() => {
    authClient
      .init({ ...(defaultInitOptions as any), ...initOptions })
      /**
       * Refresh token immediately after each keycloak init (call /token with grant_type: authorization_code),
       * in order to sync token between web and profits components in case of browser hard refresh.
       */
      .then(() => authClient.updateToken(-1))
      .catch(onError('onInitError'));
  }, [onError, initOptions, authClient]);

  useEffect(() => {
    // Attach Keycloak listeners
    if (!hasKeycloakAttachedListeners(authClient)) {
      authClient.onReady = updateState('onReady');
      authClient.onAuthError = onError('onAuthError');
      authClient.onAuthLogout = updateState('onAuthLogout');
      authClient.onAuthSuccess = updateState('onAuthSuccess');
      authClient.onTokenExpired = refreshToken('onTokenExpired');
      authClient.onAuthRefreshError = onError('onAuthRefreshError');
      authClient.onAuthRefreshSuccess = updateState('onAuthRefreshSuccess');
      // Set the auth client with attached Keycloak listeners in provider state
      dispatchCb(action(KeycloakContextActionTypes.Init, authClient));
    }
  }, [initOptions, authClient, onError, updateState, refreshToken, dispatchCb]);

  // Call dx4b post-login on each token refresh
  useInvokeSWRService<{
    cardHeaders: CardHeaders;
    selectedPartyIdentifier?: PartyIdentification;
    partyRelationships?: PartyRelationship[];
  }>(
    dashboardService.PostLoginCard,
    {
      customerId: state.selectedCustomerId,
      meta: {
        timestamp: state.authClient?.token,
      },
    },
    !!state.selectedCustomerId,
    'dashboardService.PostLoginCard',
    { revalidateOnMount: false }, // skip first request
  );

  const onSelectCustomerId = useCallback((id: string) => {
    dispatch(action(KeycloakContextActionTypes.SetSelectedCustomerId, id));
  }, []);

  return (
    <KeycloakContext.Provider
      value={{
        ...authClient,
        ...state,
        onSelectCustomerId,
      }}>
      <KeycloakDispatchContext.Provider value={dispatch}>
        {!!loaderComponent && (!state.initialized || state.isLoading) ? loaderComponent : children}
      </KeycloakDispatchContext.Provider>
    </KeycloakContext.Provider>
  );
};

export { ReactKeycloakProvider };
